import React, { Component } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { parseIsoDate } from "@formant/date-fns/parseIsoDate";
import { IInterventionRequest } from "@formant/core/model/IInterventionRequest";
import { ILabeledPolygon } from "@formant/core/model/ILabeledPolygon";

import { eventStore, userStore } from "../../../stores";
import { captureError } from "../../../utils/captureError";
import { formatDateTime } from "../../../utils/formatDateTime";
import { Form } from "../../common/Form";
import { StandardButton } from "../../common/StandardButton";
import { LabeledPolygonInput } from "./LabeledPolygonInput";
import { LabeledPolygonItem } from "./LabeledPolygonItem";
import * as styles from "./LabelingRequestDetails.scss";
import { SplitView } from "./SplitView";

export interface ILabelingRequestDetailsProps {
    request: IInterventionRequest<"labeling">;
    onRespond: () => void;
}

@observer
export class LabelingRequestDetails extends Component<ILabelingRequestDetailsProps> {
    @observable
    private value?: ILabeledPolygon[];
    @observable
    private activeIndex?: number;
    @observable
    private loading: boolean = false;

    public componentDidMount() {
        const { request } = this.props;
        const { data } = request;

        const respondedValue = request.responses?.slice(-1)[0]?.data.value;

        this.value = this.value || respondedValue || data.hint || [];
    }

    public render() {
        const { request } = this.props;
        const { data } = request;
        const { imageUrl } = data;

        const value = this.value || [];

        const response = request.responses?.slice(-1)[0];
        const responseUser = response?.userId && userStore.get(response.userId);
        const responseTime = response?.createdAt;

        const view = (
            <LabeledPolygonInput
                imageUrl={imageUrl}
                value={value}
                activeIndex={this.activeIndex}
                readOnly={!!response}
                onChange={this.onChangeValue}
                onActivate={this.onActivate}
            />
        );

        const summary = (
            <div className={styles.request}>
                <p className={styles.instructions}>{data.instruction}</p>
                {responseUser && responseTime && (
                    <div className={styles["response-details"]}>
                        Completed at{" "}
                        {formatDateTime(parseIsoDate(responseTime))} by{" "}
                        {responseUser.firstName} {responseUser.lastName}.
                    </div>
                )}
                {!response && (
                    <p className={styles.help}>
                        Click to draw points. Shift click and drag to draw a
                        box.
                    </p>
                )}
                <Form onSubmit={this.onSubmit} onLoading={this.onLoading}>
                    <div className={styles.polygons}>
                        {value.map((polygon, i) => {
                            const onDelete = () =>
                                (this.value = value.filter(_ => _ !== polygon));
                            const onMouseEnter = () => (this.activeIndex = i);
                            const onMouseLeave = () =>
                                (this.activeIndex = undefined);

                            return (
                                <LabeledPolygonItem
                                    key={i}
                                    polygon={polygon}
                                    active={this.activeIndex === i}
                                    labels={data.labels}
                                    readOnly={!!response}
                                    onDelete={onDelete}
                                    onMouseEnter={onMouseEnter}
                                    onMouseLeave={onMouseLeave}
                                />
                            );
                        })}
                    </div>
                    {!response ? (
                        <StandardButton
                            primary={true}
                            large={true}
                            disabled={!!response}
                            loading={this.loading}
                        >
                            Submit
                        </StandardButton>
                    ) : undefined}
                </Form>
            </div>
        );

        return (
            <SplitView
                className={styles["labeling-request-details"]}
                view={view}
                summary={summary}
            />
        );
    }

    private onSubmit = async () => {
        const { request, onRespond } = this.props;
        const { data } = request;
        const value = this.value || data.hint;
        try {
            if (value) {
                await eventStore.respond(request, {
                    value,
                });
            }
            onRespond();
        } catch (error) {
            captureError(error as Error);
        }
    };

    private onChangeValue = (value: ILabeledPolygon[]) => (this.value = value);

    private onActivate = (index?: number) => (this.activeIndex = index);

    private onLoading = (loading: boolean) => {
        this.loading = loading;
    };
}
